import type {
  AgentPermissionAction,
  AgentPermissionLayer,
  AgentPermissionPolicy,
  AgentPermissionPromptReply,
  AgentPermissionRequest,
  AgentPermissionRule,
  AgentPermissionRuleMatch,
} from "schemas/generated/agentPermissions";
import type { OpencodePermissionReply } from "schemas/generated/agentTools/opencode";

import isEqual from "lodash-es/isEqual";
import { filterNulls } from "replo-utils/lib/array";
import { capitalizeFirstLetter } from "replo-utils/lib/string";
import { z } from "zod";

import { knownIntegrationToolMetadataRegistry } from "./agentTools/remote/integrations";

export const agentPermissionActionSchema = z
  .enum(["allow", "ask", "deny"])
  .describe("AgentPermissionAction");

export const opencodePermissionConfigSchema = z
  .record(
    z.string(),
    z.union([
      agentPermissionActionSchema,
      z.record(z.string(), agentPermissionActionSchema),
    ]),
  )
  .describe("OpencodePermissionConfig");

export type OpencodePermissionConfig = z.infer<
  typeof opencodePermissionConfigSchema
>;

export const agentPermissionLayerSchema = z
  .enum(["platform", "user_project"])
  .describe("AgentPermissionLayer");

export const agentPermissionPromptReplySchema = z
  .enum(["allow_once", "deny_once", "always_allow", "always_deny"])
  .describe("AgentPermissionPromptReply");

const nonEmptyTrimmedStringSchema = z.string().trim().min(1);

export const agentPermissionRuleSchema = z
  .object({
    target: nonEmptyTrimmedStringSchema,
    pattern: nonEmptyTrimmedStringSchema.optional(),
    action: agentPermissionActionSchema,
  })
  .describe("AgentPermissionRule");

export const agentPermissionRuleMatchSchema = agentPermissionRuleSchema
  .extend({
    layer: agentPermissionLayerSchema,
  })
  .describe("AgentPermissionRuleMatch");

export const agentPermissionPolicySchema = z
  .object({
    rules: z.array(agentPermissionRuleSchema).default([]),
  })
  .describe("AgentPermissionPolicy");

export const agentPermissionRequestSchema = z
  .object({
    target: nonEmptyTrimmedStringSchema,
    pattern: nonEmptyTrimmedStringSchema.optional(),
  })
  .describe("AgentPermissionRequest");

export const agentPermissionResolutionSchema = z
  .object({
    action: agentPermissionActionSchema,
    matchedRules: z.array(agentPermissionRuleMatchSchema),
    winningRule: agentPermissionRuleMatchSchema.nullable(),
  })
  .describe("AgentPermissionResolution");

function getCanonicalToolNamePart({ value }: { value: string }): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/\W+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .toLowerCase();
}

// A code-mode script names an MCP tool "bedrock.replo_products_products_create"
// while its permission ask is keyed "bedrock_replo_products_products_create";
// both canonicalize to the same target.
export function getPermissionTargetForToolName({
  toolName,
}: {
  toolName: string;
}): string {
  return getCanonicalToolNamePart({ value: toolName });
}

export function getAgentPermissionToolLabel({
  toolName,
}: {
  toolName: string;
}) {
  return capitalizeFirstLetter(toolName.replaceAll("_", " "));
}

export function areAgentPermissionScopesEquivalent({
  candidate,
  selected,
}: {
  candidate: { messageId: string; patterns: string[]; permission: string };
  selected: { messageId: string; patterns: string[]; permission: string };
}) {
  return (
    candidate.messageId === selected.messageId &&
    candidate.permission === selected.permission &&
    isEqual(candidate.patterns, selected.patterns)
  );
}

const opencodePermissionReplyByPromptReply: Record<
  AgentPermissionPromptReply,
  OpencodePermissionReply
> = {
  allow_once: "once",
  always_allow: "always",
  deny_once: "reject",
  always_deny: "reject",
};

export function getOpencodePermissionReply({
  reply,
}: {
  reply: AgentPermissionPromptReply;
}): OpencodePermissionReply {
  return opencodePermissionReplyByPromptReply[reply];
}

export function getIntegrationToolPermissionTarget({
  integrationKey,
  toolName,
}: {
  integrationKey: string;
  toolName: string;
}) {
  return `bedrock_${getCanonicalToolNamePart({
    value: integrationKey,
  })}_${getCanonicalToolNamePart({ value: toolName })}`;
}

// Permission targets and integration tool names carry an internal `bedrock_`
// namespace prefix that must never surface in user-facing copy. Integration
// tools resolve to a "<Integration> <tool>" label from the registry (e.g.
// "Linear delete issue"); everything else drops the prefix before formatting.
const integrationToolPermissionLabelByTarget = new Map<string, string>(
  Object.entries(knownIntegrationToolMetadataRegistry).flatMap(
    ([integrationKey, integration]) => {
      return Object.keys(integration.tools).map((toolName) => {
        return [
          getIntegrationToolPermissionTarget({ integrationKey, toolName }),
          `${integration.name} ${toolName.replaceAll("_", " ")}`,
        ] as const;
      });
    },
  ),
);

export function getAgentPermissionDisplayLabel({
  permission,
}: {
  permission: string;
}): string {
  const integrationToolLabel =
    integrationToolPermissionLabelByTarget.get(permission);
  if (integrationToolLabel) {
    return integrationToolLabel;
  }
  return getAgentPermissionToolLabel({
    toolName: permission.replace(/^bedrock_/, ""),
  });
}

// What a permission lets the agent do, in the customer's words. Opencode's
// own permission ids are verbs about files and commands; anything else is an
// integration or product tool with a display label.
export function getAgentPermissionDescription({
  permission,
}: {
  permission: string;
}): string {
  return (
    {
      bash: "Run a command",
      shell: "Run a command",
      edit: "Change a project file",
      write: "Change a project file",
      patch: "Change a project file",
      read: "Read a project file",
    }[permission.trim().toLowerCase()] ??
    `Use ${getAgentPermissionDisplayLabel({ permission })}`
  );
}

type ReploProductsTools =
  (typeof knownIntegrationToolMetadataRegistry)["replo-products"]["tools"];

// Derived from the generated registry, so gating a new product tool fails
// typecheck here until it gets prompt copy.
export type ReploProductToolName = {
  [ToolName in keyof ReploProductsTools]: ReploProductsTools[ToolName] extends {
    requiresPermission: true;
  }
    ? ToolName
    : never;
}[keyof ReploProductsTools];

// The derived label names a tool ("Products set variants"), not an action, so
// first-party product tools spell out the edit on every permission surface.
export const REPLO_PRODUCT_TOOL_LABELS: Record<ReploProductToolName, string> = {
  orders_create_draft: "Create a fulfillment draft",
  products_create: "Create a product",
  products_create_from_catalog: "Add a print-on-demand product",
  products_delete: "Delete a product",
  products_duplicate: "Duplicate a product",
  products_place_design: "Put a design on a product",
  products_render_mockups: "Generate product mockups",
  products_save_mockups: "Save product mockups",
  products_set_inventory: "Change product stock",
  products_set_media: "Change product photos",
  products_set_variants: "Change product options and variants",
  products_update: "Change product details",
};

// `satisfies` rejects names the registry doesn't gate; a name missing from
// this list fails the runtime mirror tests instead.
export const REPLO_PRODUCT_TOOL_NAMES = [
  "orders_create_draft",
  "products_create",
  "products_create_from_catalog",
  "products_delete",
  "products_duplicate",
  "products_place_design",
  "products_render_mockups",
  "products_save_mockups",
  "products_set_inventory",
  "products_set_media",
  "products_set_variants",
  "products_update",
] as const satisfies readonly ReploProductToolName[];

export type IntegrationToolRequiringPermission = {
  integrationKey: string;
  integrationId: string;
  integrationName: string;
  integrationImage: string | null;
  toolName: string;
  target: string;
};

export function getIntegrationToolsRequiringPermission(): IntegrationToolRequiringPermission[] {
  return Object.entries(knownIntegrationToolMetadataRegistry).flatMap(
    ([integrationKey, integration]) => {
      return Object.entries(integration.tools)
        .filter(([_toolName, tool]) => {
          return "requiresPermission" in tool && tool.requiresPermission;
        })
        .map(([toolName]) => {
          return {
            integrationKey,
            integrationId: integration.key,
            integrationName: integration.name,
            integrationImage: integration.image,
            toolName,
            target: getIntegrationToolPermissionTarget({
              integrationKey,
              toolName,
            }),
          };
        });
    },
  );
}

function getIntegrationToolPermissionRulesToAsk(): AgentPermissionRule[] {
  return getIntegrationToolsRequiringPermission().map(({ target }) => {
    return { target, action: "ask" };
  });
}

export const INTERNAL_PERMISSION_TARGETS = [
  "publish",
  "bedrock_delete_site",
  "bedrock_delete_custom_domain",
  "bedrock_add_project_membership",
  "bedrock_remove_project_membership",
  "bedrock_add_workspace_membership",
  "bedrock_remove_workspace_membership",
  // Creates a real Stripe connected account for the project.
  "bedrock_set_up_checkout_payments",
  // DataForSEO tools whose cost scales with input or that spawn paid async
  // tasks; calls spend from Replo's DataForSEO account.
  "bedrock_seo_serp_search",
  "bedrock_seo_domain_ranked_keywords",
  "bedrock_seo_backlinks_list",
  "bedrock_seo_llm_mentions",
  "bedrock_seo_site_audit_start",
  "bedrock_seo_reviews_start",
  "bedrock_seo_merchant_products_start",
] as const;

export type InternalPermissionTarget =
  (typeof INTERNAL_PERMISSION_TARGETS)[number];

export function isInternalPermissionTarget(
  target: string,
): target is InternalPermissionTarget {
  return INTERNAL_PERMISSION_TARGETS.some(
    (internalTarget) => internalTarget === target,
  );
}

const DEFAULT_PLATFORM_AGENT_PERMISSION_POLICY =
  agentPermissionPolicySchema.parse({
    rules: [
      {
        target: "*",
        action: "allow",
      },
      ...getIntegrationToolPermissionRulesToAsk(),
      ...INTERNAL_PERMISSION_TARGETS.map((target) => ({
        target,
        action: "ask" as const,
      })),
    ],
  });

export function getDefaultPlatformAgentPermissionPolicy() {
  return structuredClone(DEFAULT_PLATFORM_AGENT_PERMISSION_POLICY);
}

function isPatternMatchingValue({
  pattern,
  value,
}: {
  pattern: string;
  value: string;
}): boolean {
  let patternIndex = 0;
  let valueIndex = 0;
  let starIndex = -1;
  let valueIndexAfterStar = 0;

  while (valueIndex < value.length) {
    const patternChar = pattern[patternIndex];
    if (patternChar === value[valueIndex] || patternChar === "?") {
      patternIndex += 1;
      valueIndex += 1;
      continue;
    }
    if (patternChar === "*") {
      starIndex = patternIndex;
      valueIndexAfterStar = valueIndex;
      patternIndex += 1;
      continue;
    }
    if (starIndex !== -1) {
      patternIndex = starIndex + 1;
      valueIndexAfterStar += 1;
      valueIndex = valueIndexAfterStar;
      continue;
    }
    return false;
  }

  while (pattern[patternIndex] === "*") {
    patternIndex += 1;
  }

  return patternIndex === pattern.length;
}

function isRuleMatchingRequest({
  request,
  rule,
}: {
  request: AgentPermissionRequest;
  rule: AgentPermissionRule;
}): boolean {
  const targetMatches = rule.target === "*" || rule.target === request.target;
  if (!targetMatches) {
    return false;
  }
  if (!rule.pattern) {
    return true;
  }
  if (!request.pattern) {
    return false;
  }
  return isPatternMatchingValue({
    pattern: rule.pattern,
    value: request.pattern,
  });
}

function getLastMatchingAgentPermissionRuleMatch({
  layer,
  policy,
  request,
}: {
  layer: AgentPermissionLayer;
  policy: AgentPermissionPolicy;
  request: AgentPermissionRequest;
}): AgentPermissionRuleMatch | null {
  const matchingRule =
    policy.rules.findLast((rule) => {
      return isRuleMatchingRequest({ request, rule });
    }) ?? null;
  return matchingRule ? { ...matchingRule, layer } : null;
}

function getWinningAgentPermissionRule({
  platformRule,
  userProjectRule,
}: {
  platformRule: AgentPermissionRuleMatch | null;
  userProjectRule: AgentPermissionRuleMatch | null;
}): AgentPermissionRuleMatch | null {
  if (platformRule?.action === "deny") {
    return platformRule;
  }
  if (userProjectRule) {
    return userProjectRule;
  }
  if (platformRule) {
    return platformRule;
  }
  return null;
}

export function getAgentPermissionResolution({
  platformPolicy,
  request,
  userProjectPolicy,
}: {
  platformPolicy: AgentPermissionPolicy;
  request: AgentPermissionRequest;
  userProjectPolicy: AgentPermissionPolicy;
}) {
  const parsedRequest = agentPermissionRequestSchema.parse(request);
  const parsedPlatformPolicy =
    agentPermissionPolicySchema.parse(platformPolicy);
  const parsedUserProjectPolicy =
    agentPermissionPolicySchema.parse(userProjectPolicy);
  const platformRule = getLastMatchingAgentPermissionRuleMatch({
    layer: "platform",
    policy: parsedPlatformPolicy,
    request: parsedRequest,
  });
  const userProjectRule = getLastMatchingAgentPermissionRuleMatch({
    layer: "user_project",
    policy: parsedUserProjectPolicy,
    request: parsedRequest,
  });
  const matchingRules = filterNulls([platformRule, userProjectRule]);
  const winningRule = getWinningAgentPermissionRule({
    platformRule,
    userProjectRule,
  });

  return agentPermissionResolutionSchema.parse({
    action: winningRule?.action ?? "ask",
    matchedRules: matchingRules,
    winningRule,
  });
}

function getOpencodePermissionTarget({ target }: { target: string }): string {
  return target === "file.edit" ? "edit" : target;
}

const DEPENDENCY_INSTALL_BASH_RULES: AgentPermissionRule[] = [
  { target: "bash", pattern: "npm install *", action: "deny" },
  { target: "bash", pattern: "npm add *", action: "deny" },
  { target: "bash", pattern: "yarn install *", action: "deny" },
  { target: "bash", pattern: "yarn add *", action: "deny" },
  { target: "bash", pattern: "pnpm install *", action: "deny" },
  { target: "bash", pattern: "pnpm add *", action: "deny" },
];

function getRequestFromRule({
  rule,
}: {
  rule: AgentPermissionRule;
}): AgentPermissionRequest {
  return {
    target: rule.target,
    ...(rule.pattern ? { pattern: rule.pattern } : {}),
  };
}

function getUserProjectRulesAllowedByPlatform({
  platformPolicy,
  userProjectPolicy,
}: {
  platformPolicy: AgentPermissionPolicy;
  userProjectPolicy: AgentPermissionPolicy;
}) {
  return userProjectPolicy.rules.filter((rule) => {
    const resolution = getAgentPermissionResolution({
      platformPolicy,
      request: getRequestFromRule({ rule }),
      userProjectPolicy: { rules: [rule] },
    });
    return resolution.winningRule?.layer === "user_project";
  });
}

function getOpencodeRulesFromAgentPermissionRule({
  rule,
}: {
  rule: AgentPermissionRule;
}): AgentPermissionRule[] {
  if (rule.target === "dependency.install") {
    return rule.action === "deny" ? DEPENDENCY_INSTALL_BASH_RULES : [];
  }
  if (
    rule.target === "file.edit" &&
    rule.pattern === "*" &&
    rule.action === "allow"
  ) {
    return [];
  }
  return [rule];
}

function addOpencodePermissionRule({
  permission,
  rule,
}: {
  permission: OpencodePermissionConfig;
  rule: AgentPermissionRule;
}) {
  const target = getOpencodePermissionTarget({ target: rule.target });
  const existing = permission[target];
  if (!rule.pattern) {
    permission[target] =
      existing && typeof existing === "object"
        ? { ...existing, "*": rule.action }
        : rule.action;
    return;
  }
  let nested: Record<string, AgentPermissionAction> = {};
  if (existing && typeof existing === "object") {
    nested = { ...existing };
  } else if (existing) {
    nested = { "*": existing };
  }
  permission[target] = {
    ...nested,
    [rule.pattern]: rule.action,
  };
}

export function getOpencodePermissionConfig({
  platformPolicy,
  userProjectPolicy,
}: {
  platformPolicy: AgentPermissionPolicy;
  userProjectPolicy: AgentPermissionPolicy;
}): OpencodePermissionConfig {
  const parsedPlatformPolicy =
    agentPermissionPolicySchema.parse(platformPolicy);
  const parsedUserProjectPolicy =
    agentPermissionPolicySchema.parse(userProjectPolicy);
  const rules = [
    ...parsedPlatformPolicy.rules,
    ...getUserProjectRulesAllowedByPlatform({
      platformPolicy: parsedPlatformPolicy,
      userProjectPolicy: parsedUserProjectPolicy,
    }),
  ];
  const opencodeRules = rules.flatMap((rule) => {
    return getOpencodeRulesFromAgentPermissionRule({ rule });
  });
  const permission: OpencodePermissionConfig = {};
  for (const rule of opencodeRules) {
    addOpencodePermissionRule({ permission, rule });
  }
  return permission;
}

export const agentPermissionScopeSchema = z
  .object({
    permission: z.string(),
    patterns: z.array(z.string()),
  })
  .describe("AgentPermissionScope");

export function arePermissionRequestsCovered({
  requests,
  scopes,
}: {
  requests: { permission: string; patterns: string[] }[];
  scopes: { permission: string; patterns: string[] }[];
}) {
  return requests.every((request) => {
    return scopes.some((scope) => {
      return (
        scope.permission === request.permission &&
        isEqual(scope.patterns, request.patterns)
      );
    });
  });
}
